import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMeeting, useParticipant, usePubSub, VideoPlayer } from "../config/sdkConfig";
import ReactPlayer from "react-player";
import MicOffSmallIcon from "../icons/MicOffSmallIcon";
import ScreenShareIcon from "../icons/ScreenShareIcon";
import SpeakerIcon from "../icons/SpeakerIcon";
import { nameTructed } from "../utils/helper";
import { CornerDisplayName, ParticipantView } from "./ParticipantView";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import { useSiteContext } from "../context/SiteContext";
import useIsMobile from "../hooks/useIsMobile";
import AgoraContext from "../context/AgoraContext";

const PIP_WIDTH = 240;
const PIP_HEIGHT = 135;
const PIP_HEADER_HEIGHT = 24;
const PIP_MARGIN = 16;
// Key bumped to v2 when default position moved from bottom-right to top-right —
// invalidates any stale saved positions so existing users see the new default once.
const PIP_STORAGE_KEY = "rlink:presenterScreenPipPosition_v2";

// Large inline screen preview shown in the "You are presenting" area.
// Shows the presenter exactly what participants see — just the shared screen content.
// (Camera self-view is rendered separately as a draggable PresenterCameraPiP overlay.)
function PresenterScreenPreview({ localScreenTrack }) {
  const previewVideoRef = useRef(null);

  useEffect(() => {
    if (!previewVideoRef.current || !localScreenTrack) return;
    try {
      const track = localScreenTrack.getMediaStreamTrack();
      if (track) previewVideoRef.current.srcObject = new MediaStream([track]);
    } catch (err) {
      console.error("[PresenterPreview] Screen preview failed:", err);
    }
  }, [localScreenTrack]);

  return (
    <div className="w-full max-w-4xl rounded-xl overflow-hidden border border-white/10 shadow-2xl bg-black relative" style={{ aspectRatio: '16/9' }}>
      <video
        ref={previewVideoRef}
        autoPlay
        muted
        playsInline
        style={{ width: "100%", height: "100%", objectFit: "contain", backgroundColor: "#000" }}
      />
    </div>
  );
}

// Draggable floating preview of the presenter's own SHARED SCREEN, shown only to the
// presenter while they're sharing. Lets them monitor what their viewers see.
//
// Two modes:
//  1. **In-browser draggable** (default) — a floating pill rendered via createPortal,
//     stays inside the meeting tab. Reliable, no native APIs.
//  2. **Native browser PiP** — when the user clicks the "Pop out" button, we call
//     `requestPictureInPicture()` from inside the click handler so the user-gesture
//     context is preserved. The browser pops the video into its OS-level PiP window
//     which floats over all apps.
//
// Critical safety rules (from prior debugging — see pip_screen_share_status.md):
//  - **NEVER call .stop() on the MediaStreamTrack** — it's the published Agora track
//    and stopping it kills the share for everyone.
//  - **NEVER set srcObject = null** while screen share is active — same reason: it can
//    cascade a track-ended event into Agora's onTrackEnded → disableScreenShare.
//  - On unmount, just leave srcObject bound; React's GC will release the <video> element
//    without touching the track.
function PresenterScreenPiP({ localScreenTrack }) {
  const videoRef = useRef(null);
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0 });
  // Document PiP window reference (Chrome's modern API). When non-null, a separate
  // browser window is open with its own throwaway <video> wrapping the same screen track.
  const docPipWindowRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isInNativePiP, setIsInNativePiP] = useState(false);
  const [position, setPosition] = useState(() => {
    if (typeof window === "undefined") return { x: 0, y: 0 };
    try {
      const stored = window.localStorage?.getItem(PIP_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed.x === "number" && typeof parsed.y === "number") {
          return {
            x: Math.max(0, Math.min(window.innerWidth - PIP_WIDTH, parsed.x)),
            y: Math.max(0, Math.min(window.innerHeight - (PIP_HEIGHT + PIP_HEADER_HEIGHT), parsed.y)),
          };
        }
      }
    } catch (_) { /* ignore */ }
    // Default: top-right corner with a small margin (clear of the meeting top bar).
    return {
      x: Math.max(0, window.innerWidth - PIP_WIDTH - PIP_MARGIN),
      y: PIP_MARGIN + 72, // 72px keeps it below the meeting top bar
    };
  });

  // Persist position whenever it changes
  useEffect(() => {
    try {
      window.localStorage?.setItem(PIP_STORAGE_KEY, JSON.stringify(position));
    } catch (_) { /* ignore */ }
  }, [position]);

  // Open Chrome's Document PiP window and render a SECOND <video> inside it that
  // wraps the same screen-share track. The in-tab <video> stays put — React stays
  // in control of it — and the PiP window has its own throwaway video element that
  // gets GC'd when the window closes. MediaStreamTracks support multiple simultaneous
  // consumers, so two MediaStreams pointing at the same track is safe.
  const openDocPipWindow = useCallback(async () => {
    if (typeof window === "undefined") return false;
    const docPip = window.documentPictureInPicture;
    if (!docPip || typeof docPip.requestWindow !== "function") return false;
    if (!localScreenTrack) return false;
    if (docPipWindowRef.current && !docPipWindowRef.current.closed) return true;

    const popW = 360;
    const popH = 220;
    let pipWindow;
    try {
      pipWindow = await docPip.requestWindow({ width: popW, height: popH });
    } catch (err) {
      console.log("[PresenterScreenPiP] Document PiP requestWindow failed:", err.message);
      return false;
    }
    docPipWindowRef.current = pipWindow;

    // Style the PiP window body and create a fresh <video> element inside it.
    // This is a SEPARATE video from the in-tab one — they share the underlying track
    // via a new MediaStream wrapper, which is safe (tracks support multiple consumers).
    try {
      const doc = pipWindow.document;
      doc.title = "Your shared screen";
      doc.body.style.margin = "0";
      doc.body.style.padding = "0";
      doc.body.style.backgroundColor = "#000";
      doc.body.style.overflow = "hidden";

      const popVideo = doc.createElement("video");
      popVideo.autoplay = true;
      popVideo.muted = true;
      popVideo.playsInline = true;
      popVideo.style.width = "100%";
      popVideo.style.height = "100%";
      popVideo.style.objectFit = "contain";
      popVideo.style.backgroundColor = "#000";

      try {
        const track = localScreenTrack.getMediaStreamTrack();
        if (track && track.readyState === "live") {
          popVideo.srcObject = new MediaStream([track]);
        }
      } catch (err) {
        console.warn("[PresenterScreenPiP] Failed to bind track to PiP video:", err);
      }
      doc.body.appendChild(popVideo);
      popVideo.play().catch(() => { /* harmless */ });
    } catch (err) {
      console.warn("[PresenterScreenPiP] Failed to set up PiP window contents:", err);
    }

    // Try to position top-right of the screen. moveTo() may be silently ignored on
    // some platforms but it's the only standard way to influence PiP placement.
    try {
      const sw = window.screen?.availWidth || window.innerWidth;
      const margin = 16;
      pipWindow.moveTo(Math.max(0, sw - popW - margin), margin);
    } catch (_) { /* ignore */ }

    // Clean up our refs when the PiP window closes. The throwaway video element
    // is GC'd along with the window — we never touch it.
    const onPipClose = () => {
      docPipWindowRef.current = null;
      setIsInNativePiP(false);
    };
    pipWindow.addEventListener("pagehide", onPipClose, { once: true });

    setIsInNativePiP(true);
    return true;
  }, [localScreenTrack]);

  // Wire the screen capture track into the <video>. We use the proven
  // `getMediaStreamTrack()` + `new MediaStream([track])` pattern. Re-runs only when
  // the track itself changes — never on PiP open/close, never on minimize, so the
  // track-ended cascade can't fire from this effect.
  useEffect(() => {
    if (!videoRef.current || !localScreenTrack) return;
    const v = videoRef.current;
    try {
      const track = localScreenTrack.getMediaStreamTrack();
      if (track && track.readyState === "live") {
        v.srcObject = new MediaStream([track]);
        // Opt-in to Chrome's auto-PiP. When set on a playing video element, the browser
        // automatically pops it into PiP when the user switches to another tab or app,
        // and brings it back when they return.
        try { v.autoPictureInPicture = true; } catch (_) { /* not all browsers */ }
        v.play().catch(() => { /* harmless */ });

        // Default: pop out into a floating window immediately so the presenter sees
        // the shared screen as a top-right floating window from the start. Try Document
        // PiP first (lets us position via moveTo); fall back to standard video PiP if
        // unavailable. The screen-share button click that triggered this code path
        // satisfies Chrome's user-gesture requirement (transient activation, ~5s window).
        let cancelled = false;
        const tryAutoPopOut = async () => {
          if (cancelled) return;
          if (v.readyState < 2) {
            await new Promise((resolve) => {
              const t = setTimeout(resolve, 800);
              v.addEventListener("loadeddata", () => { clearTimeout(t); resolve(); }, { once: true });
            });
            if (cancelled) return;
          }

          // Prefer Document PiP (positionable, top-right) ...
          const docPipOk = await openDocPipWindow();
          if (docPipOk) return;

          // ... fall back to standard video PiP (browser chooses position).
          if (!document.pictureInPictureEnabled) return;
          if (document.pictureInPictureElement === v) return;
          try {
            await v.requestPictureInPicture();
          } catch (err) {
            console.log("[PresenterScreenPiP] Auto pop-out skipped:", err.message);
          }
        };
        tryAutoPopOut();
        return () => { cancelled = true; };
      }
    } catch (err) {
      console.warn("[PresenterScreenPiP] Failed to attach screen track:", err);
    }
    // NOTE: no srcObject cleanup — see safety rules above.
  }, [localScreenTrack, openDocPipWindow]);

  // Track native PiP exit so we can update our button state. Just flip a flag —
  // don't touch srcObject. The video stays bound to the screen track and the
  // in-browser preview keeps showing.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onEnter = () => setIsInNativePiP(true);
    const onLeave = () => setIsInNativePiP(false);
    v.addEventListener("enterpictureinpicture", onEnter);
    v.addEventListener("leavepictureinpicture", onLeave);
    return () => {
      v.removeEventListener("enterpictureinpicture", onEnter);
      v.removeEventListener("leavepictureinpicture", onLeave);
    };
  }, []);

  // Close any open Document PiP window AND exit standard video PiP whenever the
  // screen-share track goes away (host clicks Stop Presenting, OS-level "Stop sharing"
  // button, or the track ends for any reason). Without this the PiP window lingers
  // showing a black frame after the share has stopped.
  useEffect(() => {
    if (localScreenTrack) {
      // Also listen to the underlying track for browser-level "Stop sharing" clicks.
      // Agora's localScreenTrack proxies the MediaStreamTrack — when the user hits the
      // Chrome "Stop sharing" pill, the track ends and we should close the PiP too.
      let mediaTrack;
      try { mediaTrack = localScreenTrack.getMediaStreamTrack?.(); } catch (_) { /* ignore */ }
      const onTrackEnded = () => {
        try { docPipWindowRef.current?.close(); } catch (_) { /* ignore */ }
        docPipWindowRef.current = null;
        if (typeof document !== "undefined" && document.pictureInPictureElement === videoRef.current) {
          document.exitPictureInPicture().catch(() => { /* ignore */ });
        }
        setIsInNativePiP(false);
      };
      mediaTrack?.addEventListener?.("ended", onTrackEnded);
      return () => {
        mediaTrack?.removeEventListener?.("ended", onTrackEnded);
      };
    }
    // localScreenTrack is null/undefined → screen share has ended. Close the PiP window.
    try { docPipWindowRef.current?.close(); } catch (_) { /* ignore */ }
    docPipWindowRef.current = null;
    if (typeof document !== "undefined" && document.pictureInPictureElement === videoRef.current) {
      document.exitPictureInPicture().catch(() => { /* ignore */ });
    }
    setIsInNativePiP(false);
    return undefined;
  }, [localScreenTrack]);

  // Manual pop-out toggle — runs inside the click handler to preserve user-gesture context.
  // Tries Document PiP (top-right positioned) first, falls back to standard video PiP.
  const handleTogglePopOut = useCallback(async (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    try {
      // Already in Document PiP — close it
      if (docPipWindowRef.current && !docPipWindowRef.current.closed) {
        try { docPipWindowRef.current.close(); } catch (_) { /* ignore */ }
        return;
      }
      // Already in standard video PiP — close it
      if (document.pictureInPictureElement === v) {
        await document.exitPictureInPicture();
        return;
      }
      // Open Document PiP first (positionable)
      const docOk = await openDocPipWindow();
      if (docOk) return;
      // Fall back to standard video PiP
      if (!document.pictureInPictureEnabled) {
        console.warn("[PresenterScreenPiP] Native PiP not supported in this browser");
        return;
      }
      if (v.readyState < 2) {
        await new Promise((resolve) => {
          const t = setTimeout(resolve, 600);
          v.addEventListener("loadeddata", () => { clearTimeout(t); resolve(); }, { once: true });
        });
      }
      await v.requestPictureInPicture();
    } catch (err) {
      console.warn("[PresenterScreenPiP] PiP toggle failed:", err.message);
    }
  }, [openDocPipWindow]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: position.x,
      startPosY: position.y,
    };
    setIsDragging(true);
    document.body.style.userSelect = "none";
  }, [position]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const w = isMinimized ? 160 : PIP_WIDTH;
      const h = isMinimized ? 32 : PIP_HEIGHT + PIP_HEADER_HEIGHT;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - w, dragRef.current.startPosX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - h, dragRef.current.startPosY + dy)),
      });
    };
    const onUp = () => {
      setIsDragging(false);
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging, isMinimized]);

  // Always render the <video> in the DOM (even when minimized) so srcObject stays
  // bound and native PiP can work. Hide the visible portal contents when minimized.
  // `autoPictureInPicture` lets Chrome auto-pop the video into a PiP window when the
  // user switches tabs or apps, no user gesture required.
  const videoEl = (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      autoPictureInPicture
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        backgroundColor: "#000",
      }}
    />
  );

  if (isMinimized) {
    return createPortal(
      <div
        className="fixed z-[2147483646] flex items-center gap-2 px-3 py-1.5 bg-black/85 backdrop-blur-sm border border-white/20 rounded-full shadow-2xl cursor-move select-none"
        style={{ left: position.x, top: position.y }}
        onMouseDown={handleMouseDown}
        title="Drag to move — click ↗ to expand"
      >
        {/* Hidden video keeps the track bound for native PiP */}
        <div style={{ position: "absolute", left: -9999, top: -9999, width: PIP_WIDTH, height: PIP_HEIGHT, pointerEvents: "none" }}>
          {videoEl}
        </div>
        <ScreenShareIcon style={{ height: 12, width: 12, color: "#00c853" }} />
        <span className="text-xs text-white/85">Your screen</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsMinimized(false);
          }}
          className="ml-1 p-0.5 rounded hover:bg-white/10 transition-colors text-white/70 text-xs"
          title="Expand"
        >
          ↗
        </button>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      className="fixed z-[2147483646] overflow-hidden bg-black/85 backdrop-blur-sm border border-white/20 rounded-lg shadow-2xl select-none"
      style={{ left: position.x, top: position.y, width: PIP_WIDTH }}
    >
      <div
        className="flex items-center justify-between px-2 bg-black/70 cursor-move"
        style={{ height: PIP_HEADER_HEIGHT }}
        onMouseDown={handleMouseDown}
        title="Drag to move"
      >
        <div className="flex items-center gap-1.5">
          <ScreenShareIcon style={{ height: 11, width: 11, color: "#00c853" }} />
          <span className="text-white text-[10px] font-medium tracking-wide">
            Your shared screen
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={handleTogglePopOut}
            className="p-1 rounded hover:bg-white/15 transition-colors text-white/80"
            title={isInNativePiP ? "Bring back into tab" : "Pop out — float over all apps"}
          >
            {/* Pop-out icon (square with arrow) */}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h6v6" />
              <path d="M10 14L21 3" />
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(true);
            }}
            className="p-0.5 rounded hover:bg-white/10 transition-colors text-white/60 text-[11px] leading-none"
            title="Minimize"
          >
            —
          </button>
        </div>
      </div>
      <div
        style={{ width: PIP_WIDTH, height: PIP_HEIGHT }}
        className="bg-[#0d1117] relative"
      >
        {videoEl}
        {isInNativePiP && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/85">
            <div className="w-12 h-12 rounded-full bg-[#6a1fbf]/30 border border-[#6a1fbf]/50 flex items-center justify-center mb-2">
              <ScreenShareIcon style={{ height: 18, width: 18, color: "#a78bfa" }} />
            </div>
            <span className="text-white/70 text-[10px]">Floating in PiP window</span>
            <button
              onClick={handleTogglePopOut}
              className="mt-2 px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-white text-[10px] transition-colors"
            >
              Bring back
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ============================================================
// PresenterCameraPiP
// ============================================================
// Draggable floating tile showing the presenter's camera, rendered for
// EVERYONE (presenter + viewers + recorder bot) whenever an element like
// a web overlay / contract / document is being presented. Without this,
// the participant grid is hidden by the element overlay and the audience
// loses sight of who's talking.
//
// Props:
//   - participantId: the Agora participant id of the presenter. Passed
//     straight through to <ParticipantView> which already handles
//     webcam-on / webcam-off / avatar placeholder.
//
// Behaviour:
//   - Rendered via createPortal to document.body so it floats over every
//     element overlay.
//   - Position persisted per-user in localStorage.
//   - Included in recordings (no `?recorder=true` guard).
//   - Viewers can hide it with the minimize pill; a small down-arrow
//     restore button reappears so they can bring it back.
// ============================================================

const CAMERA_PIP_WIDTH = 240;
const CAMERA_PIP_HEIGHT = 160;
const CAMERA_PIP_HEADER_HEIGHT = 22;
const CAMERA_PIP_STORAGE_KEY = "rlink:presenterCameraPipPosition_v2";
const CAMERA_PIP_HIDDEN_KEY = "rlink:presenterCameraPipHidden_v1";
const CAMERA_PIP_POPPED_KEY = "rlink:presenterCameraPipPopped_v1";

function initialsFromName(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0]?.toUpperCase() || "?";
}

export function PresenterCameraPiP({ participantId }) {
  const { webcamStream, webcamOn, displayName, isLocal } = useParticipant(participantId);
  const [hidden, setHidden] = useState(() => {
    try { return window.localStorage?.getItem(CAMERA_PIP_HIDDEN_KEY) === "1"; } catch { return false; }
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isInNativePiP, setIsInNativePiP] = useState(false);
  // Visible <video> — bound directly to the presenter's webcam
  // MediaStreamTrack. No wrapper component, no re-renders on unrelated
  // participant events, so the src doesn't flip between presenters.
  const videoRef = useRef(null);
  const prevTrackRef = useRef(null);
  const docPipWindowRef = useRef(null);
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0 });
  const [position, setPosition] = useState(() => {
    if (typeof window === "undefined") return { x: 16, y: 100 };
    try {
      const stored = window.localStorage?.getItem(CAMERA_PIP_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed.x === "number" && typeof parsed.y === "number") {
          return {
            x: Math.max(0, Math.min(window.innerWidth - CAMERA_PIP_WIDTH, parsed.x)),
            y: Math.max(0, Math.min(window.innerHeight - (CAMERA_PIP_HEIGHT + CAMERA_PIP_HEADER_HEIGHT), parsed.y)),
          };
        }
      }
    } catch { /* ignore */ }
    // Default: bottom-right, above the meeting bottom bar
    return {
      x: Math.max(0, window.innerWidth - CAMERA_PIP_WIDTH - 16),
      y: Math.max(0, window.innerHeight - CAMERA_PIP_HEIGHT - CAMERA_PIP_HEADER_HEIGHT - 96),
    };
  });

  useEffect(() => {
    try { window.localStorage?.setItem(CAMERA_PIP_STORAGE_KEY, JSON.stringify(position)); } catch {}
  }, [position]);

  useEffect(() => {
    try { window.localStorage?.setItem(CAMERA_PIP_HIDDEN_KEY, hidden ? "1" : "0"); } catch {}
  }, [hidden]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: position.x,
      startPosY: position.y,
    };
    setIsDragging(true);
    document.body.style.userSelect = "none";
  }, [position]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - CAMERA_PIP_WIDTH, dragRef.current.startPosX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - (CAMERA_PIP_HEIGHT + CAMERA_PIP_HEADER_HEIGHT), dragRef.current.startPosY + dy)),
      });
    };
    const onUp = () => {
      setIsDragging(false);
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging]);

  // Bind the presenter's webcam MediaStreamTrack to the visible <video>.
  // Mirrors the pattern used in FocusParticipantView — only rebuild the
  // MediaStream when the underlying track actually changes, so hot re-
  // renders don't cause the <video> to flicker or reset.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const track = webcamStream?.track;
    if (track && track.readyState === "live") {
      if (prevTrackRef.current === track) return;
      prevTrackRef.current = track;
      const ms = new MediaStream();
      ms.addTrack(track);
      v.srcObject = ms;
      try { v.autoPictureInPicture = true; } catch (_) { /* not all browsers */ }
      v.play().catch(() => { /* harmless */ });
    } else if (!track) {
      v.srcObject = null;
      prevTrackRef.current = null;
    }
  }, [webcamStream]);

  // Document PiP — preferred because it lets us position and because the
  // popped window can host HTML (name label, avatar fallback etc.). Falls
  // back to standard video PiP if unavailable.
  // Latest presenter info — kept in a ref so the PiP window's DOM
  // updater always reads fresh state without being rebuilt on every
  // parent re-render. The flicker was caused by re-running the render
  // function on EVERY render of the parent; now we only touch the DOM
  // when the actual MediaStreamTrack identity changes.
  const pipStateRef = useRef({ track: null, displayName: "", isLocal: false });
  useEffect(() => {
    pipStateRef.current = {
      track: webcamStream?.track || null,
      displayName: displayName || "",
      isLocal: !!isLocal,
    };
  });

  // Track what's currently rendered inside the PiP window so we only
  // touch its DOM when things actually change.
  const pipRenderedRef = useRef({ track: null, mode: null });

  const syncPipWindow = useCallback(() => {
    const pipWindow = docPipWindowRef.current;
    if (!pipWindow || pipWindow.closed) return;
    const { track, displayName: name, isLocal: mirror } = pipStateRef.current;
    const hasLive = !!(track && track.readyState === "live");
    const desiredMode = hasLive ? "video" : "avatar";
    const doc = pipWindow.document;

    // Initial mount: set up body shell once.
    if (!doc.body.dataset.rlinkInit) {
      doc.body.dataset.rlinkInit = "1";
      doc.title = name ? `Presenter — ${name}` : "Presenter";
      // Full-viewport flex container so the avatar / video center
      // vertically as well as horizontally. Without these the <body>
      // collapses to its content height and everything hugs the top.
      if (doc.documentElement) {
        doc.documentElement.style.height = "100%";
        doc.documentElement.style.margin = "0";
      }
      doc.body.style.margin = "0";
      doc.body.style.padding = "0";
      doc.body.style.height = "100vh";
      doc.body.style.width = "100vw";
      doc.body.style.background = "#0d1117";
      doc.body.style.overflow = "hidden";
      doc.body.style.fontFamily = "system-ui,-apple-system,Segoe UI,Roboto,sans-serif";
      doc.body.style.display = "flex";
      doc.body.style.alignItems = "center";
      doc.body.style.justifyContent = "center";
      doc.body.style.position = "relative";
      // Persistent label element — only the .textContent changes later
      const label = doc.createElement("div");
      label.id = "rlink-name";
      label.style.position = "absolute";
      label.style.left = "8px";
      label.style.bottom = "8px";
      label.style.padding = "2px 8px";
      label.style.background = "rgba(0,0,0,0.6)";
      label.style.color = "#fff";
      label.style.fontSize = "11px";
      label.style.borderRadius = "4px";
      doc.body.appendChild(label);
    }

    // Update name label without rebuilding the DOM.
    const label = doc.getElementById("rlink-name");
    if (label) {
      label.textContent = name || "";
      label.style.display = name ? "block" : "none";
    }

    // Swap the main content only if the mode OR the track identity changed.
    if (
      pipRenderedRef.current.mode !== desiredMode ||
      pipRenderedRef.current.track !== track
    ) {
      // Remove previous main content but keep the name label.
      const existing = doc.getElementById("rlink-main");
      if (existing) existing.remove();

      if (hasLive) {
        const popVideo = doc.createElement("video");
        popVideo.id = "rlink-main";
        popVideo.autoplay = true;
        popVideo.muted = true;
        popVideo.playsInline = true;
        popVideo.style.width = "100%";
        popVideo.style.height = "100%";
        popVideo.style.objectFit = "cover";
        if (mirror) popVideo.style.transform = "scaleX(-1)";
        popVideo.srcObject = new MediaStream([track]);
        doc.body.appendChild(popVideo);
        popVideo.play().catch((err) => {
          console.warn("[PresenterCameraPiP] pop video play() failed:", err.message);
        });
      } else {
        const avatarWrap = doc.createElement("div");
        avatarWrap.id = "rlink-main";
        avatarWrap.style.display = "flex";
        avatarWrap.style.flexDirection = "column";
        avatarWrap.style.alignItems = "center";
        avatarWrap.style.justifyContent = "center";
        avatarWrap.style.color = "#fff";
        const circle = doc.createElement("div");
        circle.textContent = initialsFromName(name);
        circle.style.width = "64px";
        circle.style.height = "64px";
        circle.style.borderRadius = "50%";
        circle.style.display = "flex";
        circle.style.alignItems = "center";
        circle.style.justifyContent = "center";
        circle.style.background = "linear-gradient(135deg,#6a1fbf,#4a148c)";
        circle.style.fontSize = "24px";
        circle.style.fontWeight = "600";
        avatarWrap.appendChild(circle);
        doc.body.appendChild(avatarWrap);
      }

      pipRenderedRef.current = { track, mode: desiredMode };
    }
  }, []);

  const openDocPipWindow = useCallback(async () => {
    if (typeof window === "undefined") return false;
    const docPip = window.documentPictureInPicture;
    if (!docPip || typeof docPip.requestWindow !== "function") return false;
    if (docPipWindowRef.current && !docPipWindowRef.current.closed) return true;
    const popW = CAMERA_PIP_WIDTH;
    const popH = CAMERA_PIP_HEIGHT;
    let pipWindow;
    try {
      pipWindow = await docPip.requestWindow({ width: popW, height: popH });
    } catch (err) {
      console.log("[PresenterCameraPiP] Document PiP requestWindow failed:", err.message);
      return false;
    }
    docPipWindowRef.current = pipWindow;
    pipRenderedRef.current = { track: null, mode: null };
    syncPipWindow();

    try {
      const sw = window.screen?.availWidth || window.innerWidth;
      const sh = window.screen?.availHeight || window.innerHeight;
      pipWindow.moveTo(Math.max(0, sw - popW - 16), Math.max(0, sh - popH - 96));
    } catch (_) { /* ignore */ }

    const onPipClose = () => {
      docPipWindowRef.current = null;
      pipRenderedRef.current = { track: null, mode: null };
      setIsInNativePiP(false);
      try { window.localStorage?.setItem(CAMERA_PIP_POPPED_KEY, "0"); } catch {}
    };
    pipWindow.addEventListener("pagehide", onPipClose, { once: true });
    setIsInNativePiP(true);
    try { window.localStorage?.setItem(CAMERA_PIP_POPPED_KEY, "1"); } catch {}
    return true;
  }, [syncPipWindow]);

  // When the presenter's track identity, name, or mirror flag changes,
  // update the PiP window in place — no full DOM rebuild, so no flicker.
  useEffect(() => {
    if (docPipWindowRef.current && !docPipWindowRef.current.closed) {
      syncPipWindow();
    }
  }, [webcamStream?.track, displayName, isLocal, syncPipWindow]);

  const handleTogglePopOut = useCallback(async (e) => {
    e?.stopPropagation?.();
    const v = videoRef.current;
    try {
      if (docPipWindowRef.current && !docPipWindowRef.current.closed) {
        try { docPipWindowRef.current.close(); } catch (_) { /* ignore */ }
        return;
      }
      if (v && document.pictureInPictureElement === v) {
        await document.exitPictureInPicture();
        return;
      }
      const docOk = await openDocPipWindow();
      if (docOk) return;
      if (!v || !document.pictureInPictureEnabled) return;
      if (v.readyState < 2) {
        await new Promise((resolve) => {
          const t = setTimeout(resolve, 600);
          v.addEventListener("loadeddata", () => { clearTimeout(t); resolve(); }, { once: true });
        });
      }
      await v.requestPictureInPicture();
    } catch (err) {
      console.warn("[PresenterCameraPiP] pop-out toggle failed:", err.message);
    }
  }, [openDocPipWindow]);

  // Auto-popout: Chrome's Document Picture-in-Picture requires a user
  // activation (a recent click / keypress / touch) to succeed. By the
  // time this component mounts — after socket round-trips, state updates,
  // and re-renders — the activation from the "Present" click is gone.
  //
  // Strategy: try once on mount (works in some browsers and when the
  // element presentation is a direct React click), and if that fails,
  // wait for the NEXT user gesture anywhere on the page and retry.
  // Most presenters click somewhere within a second of presenting, so
  // this produces a near-instant auto-popout that doesn't violate
  // Chrome's activation rules.
  const autoPopoutTriedRef = useRef(false);
  useEffect(() => {
    if (autoPopoutTriedRef.current) return;
    // Fire auto-popout even when webcam is off — the popped window will
    // render the avatar placeholder until the presenter turns on their
    // camera, then the track-change effect above swaps in live video.
    if (!participantId) return;
    // Recorder mode: skip auto-popout. In headless Xvfb Chrome, Document PiP
    // opens a second window that partially renders into the captured display
    // (black box with garbled text). The inline floating <video> below still
    // renders within the cell via the transform containing block — that's
    // what ends up in the final recording.
    const isRecorderPage = typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('recorder') === 'true';
    if (isRecorderPage) return;
    let wasDismissed = false;
    try { wasDismissed = window.localStorage?.getItem(CAMERA_PIP_POPPED_KEY) === "0"; } catch {}
    if (wasDismissed) return;
    autoPopoutTriedRef.current = true;
    let disposed = false;

    const tryPopout = async () => {
      if (disposed) return false;
      if (docPipWindowRef.current && !docPipWindowRef.current.closed) return true;
      try {
        const ok = await openDocPipWindow();
        return !!ok;
      } catch {
        return false;
      }
    };

    // First attempt — sync path, may succeed on some browsers.
    (async () => {
      const ok = await tryPopout();
      if (ok || disposed) return;
      // Fall back: wait for the user's next gesture and retry.
      const handler = async () => {
        await tryPopout();
        cleanup();
      };
      const cleanup = () => {
        window.removeEventListener("click", handler, true);
        window.removeEventListener("keydown", handler, true);
        window.removeEventListener("pointerdown", handler, true);
      };
      window.addEventListener("click", handler, true);
      window.addEventListener("keydown", handler, true);
      window.addEventListener("pointerdown", handler, true);
      // Stop listening after 30s — we don't want stale handlers hanging
      // around if the user never clicks.
      setTimeout(cleanup, 30000);
    })();

    return () => { disposed = true; };
  }, [participantId, openDocPipWindow]);

  // Close the PiP window when we unmount (element is closed, presenter left, etc).
  useEffect(() => {
    return () => {
      try { docPipWindowRef.current?.close(); } catch (_) { /* ignore */ }
      docPipWindowRef.current = null;
      const v = videoRef.current;
      if (v && typeof document !== "undefined" && document.pictureInPictureElement === v) {
        document.exitPictureInPicture().catch(() => { /* ignore */ });
      }
    };
  }, []);

  if (!participantId) return null;

  // Popped-out: the camera is already floating in its own always-on-top
  // window, so nothing to render in-tab. Only the popped window is
  // visible. Its `pagehide` handler flips isInNativePiP back to false,
  // which re-mounts the small "Show presenter" pill below.
  if (isInNativePiP) return null;

  // Not popped out yet: render ONLY a small pill that triggers popout
  // on click. This avoids double <video>/flicker and matches the user's
  // ask for "floating one only". Chrome's auto-popout attempt fires on
  // the next user gesture anywhere, so in most cases this pill only
  // flashes briefly before disappearing as the popped window takes over.
  if (hidden) {
    return createPortal(
      <button
        onClick={() => setHidden(false)}
        className="fixed z-[2147483646] flex items-center gap-1.5 px-3 py-1.5 bg-[#6a1fbf] hover:bg-[#5a19a0] text-white text-xs font-medium rounded-full shadow-2xl transition-colors"
        style={{ right: 16, bottom: 96 }}
        title="Show presenter"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
        Presenter
      </button>,
      document.body
    );
  }

  // Not popped yet — show a small pill that triggers popout on click.
  // Auto-popout will usually fire on the user's next gesture and replace
  // this pill with the floating PiP window. Intentionally no embedded
  // <video> to avoid the duplicate-consumer blinking.
  return createPortal(
    <button
      onClick={handleTogglePopOut}
      className="fixed z-[2147483646] flex items-center gap-1.5 px-3 py-1.5 bg-[#6a1fbf] hover:bg-[#5a19a0] text-white text-xs font-medium rounded-full shadow-2xl transition-colors"
      style={{ right: 16, bottom: 96 }}
      title="Pop out presenter into a floating window"
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 3h6v6" />
        <path d="M10 14L21 3" />
        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
      </svg>
      Pop out presenter
    </button>,
    document.body
  );
}

// Old floating PiP components — kept as dead code reference, superseded by
// PresenterCameraPiP above. Safe to delete in a follow-up cleanup pass.
function _unused_FloatingScreenSharePiP() {
  const videoRef = useRef(null);
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0 });
  const ctx = useContext(AgoraContext);
  const localScreenTrack = ctx?.localScreenTrack;
  const [isDragging, setIsDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState({
    x: window.innerWidth - PIP_WIDTH - 80,
    y: window.innerHeight - PIP_HEIGHT - 120,
  });

  // Play screen track via raw MediaStreamTrack (safe — won't stop the published track)
  useEffect(() => {
    if (!videoRef.current || !localScreenTrack || isMinimized) return;
    try {
      const mediaStreamTrack = localScreenTrack.getMediaStreamTrack();
      if (mediaStreamTrack) {
        videoRef.current.srcObject = new MediaStream([mediaStreamTrack]);
      }
    } catch (err) {
      console.warn("[FloatingPiP] Failed to set srcObject:", err);
    }
    return () => {
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [localScreenTrack, isMinimized]);

  // Drag handlers
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      startPosX: position.x, startPosY: position.y,
    };
    setIsDragging(true);
    document.body.style.userSelect = "none";
  }, [position]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const w = isMinimized ? 160 : PIP_WIDTH;
      const h = isMinimized ? 36 : PIP_HEIGHT + 28;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - w, dragRef.current.startPosX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - h, dragRef.current.startPosY + dy)),
      });
    };
    const onUp = () => {
      setIsDragging(false);
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging, isMinimized]);

  // Minimized pill
  if (isMinimized) {
    return createPortal(
      <div
        className="fixed z-50 flex items-center gap-2 px-3 py-1.5 bg-black/80 backdrop-blur-sm border border-white/20 rounded-full shadow-2xl cursor-move select-none"
        style={{ left: position.x, top: position.y }}
        onMouseDown={handleMouseDown}
      >
        <ScreenShareIcon style={{ height: 12, width: 12, color: "#00c853" }} />
        <span className="text-xs text-white/80">Your screen</span>
        <button
          onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}
          className="ml-1 p-0.5 rounded hover:bg-white/10 transition-colors text-white/60 text-xs"
          title="Expand"
        >↗</button>
      </div>,
      document.body
    );
  }

  // Full floating preview
  return createPortal(
    <div
      className="fixed z-50 overflow-hidden bg-black/85 backdrop-blur-sm border border-white/20 rounded-lg shadow-2xl select-none"
      style={{ left: position.x, top: position.y, width: PIP_WIDTH }}
    >
      <div
        className="flex items-center justify-between px-2 h-7 bg-black/60 cursor-move"
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center gap-1.5">
          <ScreenShareIcon style={{ height: 12, width: 12, color: "#00c853" }} />
          <span className="text-white text-xs font-medium">Your shared screen</span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setIsMinimized(true); }}
          className="p-0.5 rounded hover:bg-white/10 transition-colors text-white/60 text-xs"
          title="Minimize"
        >—</button>
      </div>
      <div style={{ width: PIP_WIDTH, height: PIP_HEIGHT }}>
        <video
          ref={videoRef}
          autoPlay muted playsInline
          style={{ width: "100%", height: "100%", objectFit: "contain", backgroundColor: "#000" }}
        />
      </div>
    </div>,
    document.body
  );
}

// Floating draggable screen share preview.
// Shows the presenter what participants see (their shared screen content).
// Uses the same proven approach as ScreenShareThumbnail (video srcObject)
// but rendered via portal so it floats above all meeting UI.
// Includes native PiP "Pop out" to float on top of ALL applications.
function _unused_ScreenSharePreviewWithPiP() {
  const videoRef = useRef(null);
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0 });
  const ctx = useContext(AgoraContext);
  const localScreenTrack = ctx?.localScreenTrack;
  const [isDragging, setIsDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState({
    x: window.innerWidth - PIP_WIDTH - 80,
    y: window.innerHeight - PIP_HEIGHT - 120,
  });

  // Play the screen capture using MediaStreamTrack from Agora's localScreenTrack
  // This is the proven approach from ScreenShareThumbnail that was confirmed working
  // IMPORTANT: Do NOT set srcObject = null in cleanup — it kills the published screen share track
  useEffect(() => {
    if (!videoRef.current || !localScreenTrack || isMinimized) return;
    try {
      const mediaStreamTrack = localScreenTrack.getMediaStreamTrack();
      if (mediaStreamTrack) {
        const stream = new MediaStream([mediaStreamTrack]);
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("[PiP] Failed to play screen capture:", err);
    }
  }, [localScreenTrack, isMinimized]);

  // Drag handlers
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPosX: position.x, startPosY: position.y };
    setIsDragging(true);
    document.body.style.userSelect = "none";
  }, [position]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const w = isMinimized ? 160 : PIP_WIDTH;
      const h = isMinimized ? 36 : PIP_HEIGHT + 28;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - w, dragRef.current.startPosX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - h, dragRef.current.startPosY + dy)),
      });
    };
    const onUp = () => { setIsDragging(false); document.body.style.userSelect = ""; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [isDragging, isMinimized]);

  // Minimized pill
  if (isMinimized) {
    return createPortal(
      <div
        className="fixed z-50 flex items-center gap-2 px-3 py-1.5 bg-black/80 backdrop-blur-sm border border-white/20 rounded-full shadow-2xl cursor-move select-none"
        style={{ left: position.x, top: position.y }}
        onMouseDown={handleMouseDown}
      >
        <ScreenShareIcon style={{ height: 12, width: 12, color: "#00c853" }} />
        <span className="text-xs text-white/80">Your screen</span>
        <button onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }} className="ml-1 p-0.5 rounded hover:bg-white/10 text-white/60 text-xs" title="Expand">↗</button>
      </div>,
      document.body
    );
  }

  // Full floating preview (in-browser fallback)
  return createPortal(
    <div
      className="fixed z-50 overflow-hidden bg-black/85 backdrop-blur-sm border border-white/20 rounded-lg shadow-2xl select-none"
      style={{ left: position.x, top: position.y, width: PIP_WIDTH }}
    >
      <div className="flex items-center justify-between px-2 h-7 bg-black/60 cursor-move" onMouseDown={handleMouseDown}>
        <div className="flex items-center gap-1.5">
          <ScreenShareIcon style={{ height: 12, width: 12, color: "#00c853" }} />
          <span className="text-white text-xs font-medium">Your shared screen</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); setIsMinimized(true); }} className="p-0.5 rounded hover:bg-white/10 text-white/60 text-xs" title="Minimize">—</button>
        </div>
      </div>
      <div style={{ width: PIP_WIDTH, height: PIP_HEIGHT }}>
        <video ref={videoRef} autoPlay muted playsInline style={{ width: "100%", height: "100%", objectFit: "contain", backgroundColor: "#000" }} />
      </div>
    </div>,
    document.body
  );
}

export function PresenterView({ height, children }) {
  const mMeeting = useMeeting();
  const presenterId = mMeeting?.presenterId;
  const hasActiveScreenShare = mMeeting?.hasActiveScreenShare;
  const {
    micOn,
    webcamOn,
    isLocal,
    screenShareAudioStream,
    screenShareOn,
    screenShareStream,
    displayName,
    isActiveSpeaker,
  } = useParticipant(presenterId);
  const { metaData: presenterMetaData } = useParticipant(presenterId);
  const { sideBarMode, raisedHandsParticipants } = useMeetingAppContext();
  const { meetingRole, isCoHost } = useSiteContext();
  const { publish: publishStopScreenShare } = usePubSub("STOP_SCREEN_SHARE");
  const agoraCtx = useContext(AgoraContext);
  const localScreenTrack = agoraCtx?.localScreenTrack;
  const localVideoTrack = agoraCtx?.localVideoTrack;

  // Determine if local user (host/co-host) can stop this presenter's screen share
  const isLocalHost = meetingRole === 'moderator' || meetingRole === 'host';
  const isLocalCoHost = isCoHost(mMeeting?.localParticipant?.id);
  const isLocalHostOrCoHost = isLocalHost || isLocalCoHost;
  const isPresenterHost = presenterMetaData?.meetingRole === 'moderator' || presenterMetaData?.meetingRole === 'host';
  const isPresenterCoHost = isCoHost(presenterId);
  const canStopPresentation = !isLocal && isLocalHostOrCoHost && !isPresenterHost && (isLocalHost || !isPresenterCoHost);

  const audioPlayer = useRef();
  const containerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const isMobile = useIsMobile();

  // Pinch-to-zoom state for mobile
  const [zoomState, setZoomState] = useState({
    scale: 1,
    translateX: 0,
    translateY: 0,
  });
  const [isZooming, setIsZooming] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const touchStateRef = useRef({
    initialDistance: 0,
    initialScale: 1,
    initialTranslateX: 0,
    initialTranslateY: 0,
    lastTouchTime: 0,
    lastPanX: 0,
    lastPanY: 0,
    lastTapX: 0,
    lastTapY: 0,
    tapTimeout: null,
    hasMoved: false,
    startX: 0,
    startY: 0,
  });

  // Check if the presenter has raised their hand
  const hasRaisedHand =
    presenterId &&
    raisedHandsParticipants.some(
      (raisedHand) => raisedHand.participantId === presenterId
    );

  useEffect(() => {
    if (
      !isLocal &&
      audioPlayer.current &&
      screenShareOn &&
      screenShareAudioStream
    ) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(screenShareAudioStream.track);

      audioPlayer.current.srcObject = mediaStream;
      audioPlayer.current.play().catch((err) => {
        if (
          err.message ===
          "play() failed because the user didn't interact with the document first. https://goo.gl/xX8pDD"
        ) {
          console.error("audio" + err.message);
        }
      });
    } else if (audioPlayer.current) {
      audioPlayer.current.srcObject = null;
    }
  }, [screenShareAudioStream, screenShareOn, isLocal]);

  useEffect(() => {
    const onFsChange = () => {
      const nativeFs = document.fullscreenElement === containerRef.current;
      setIsFullscreen(nativeFs);
      if (!document.fullscreenElement) {
        setZoomState({ scale: 1, translateX: 0, translateY: 0 });
        // Ensure pseudo-fullscreen is also cleared when native exits
        setIsPseudoFullscreen(false);
        document.body.classList.remove("ios-pseudo-fullscreen");
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    document.addEventListener("msfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      document.removeEventListener("msfullscreenchange", onFsChange);
    };
  }, []);

  useEffect(() => {
    if (!screenShareOn) {
      setZoomState({ scale: 1, translateX: 0, translateY: 0 });
      // Clean up pseudo-fullscreen when screen share stops
      if (isPseudoFullscreen) {
        document.body.classList.remove("ios-pseudo-fullscreen");
        setIsFullscreen(false);
        setIsPseudoFullscreen(false);
      }
    }
  }, [screenShareOn]);

  // Auto-fullscreen on mobile when screen share starts (for viewers, not the presenter)
  useEffect(() => {
    if (isMobile && screenShareOn && !isLocal && !isFullscreen) {
      // Small delay to let the video track settle before going fullscreen
      const timer = setTimeout(() => {
        document.body.classList.add("ios-pseudo-fullscreen");
        setIsFullscreen(true);
        setIsPseudoFullscreen(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isMobile, screenShareOn, isLocal]);

  // Early return if no presenter or no active screen share
  // This handles the case when the presenter leaves the meeting
  // NOTE: Must be placed AFTER all hooks to comply with React rules
  if (!presenterId || !hasActiveScreenShare) {
    console.log('[PresenterView] No presenter or no active screen share, returning null');
    return null;
  }

  // If presenter is set but screen share stream isn't ready yet, show a loading state
  // instead of returning null — this handles mobile where track arrival is delayed
  if (!screenShareOn && !isLocal) {
    console.log('[PresenterView] Remote presenter has no screenShareOn — showing loading state');
    return (
      <div
        style={{ height: height || "100%", width: "100%" }}
        className="flex flex-col items-center justify-center bg-[#001233] rounded-xl"
      >
        <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin mb-3" />
        <p className="text-white/70 text-sm font-medium">
          {displayName || 'Presenter'} is sharing their screen
        </p>
        <p className="text-white/40 text-xs mt-1">Loading screen share...</p>
      </div>
    );
  }

  const getDistance = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getCenter = (touch1, touch2) => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  };

  const constrainTranslate = (
    scale,
    translateX,
    translateY,
    containerWidth,
    containerHeight
  ) => {
    if (!videoContainerRef.current) return { translateX, translateY };

    const videoElement = videoContainerRef.current.querySelector("video");
    if (!videoElement) return { translateX, translateY };

    const videoWidth = videoElement.videoWidth || containerWidth;
    const videoHeight = videoElement.videoHeight || containerHeight;
    const scaledWidth = videoWidth * scale;
    const scaledHeight = videoHeight * scale;

    const maxTranslateX = Math.max(0, (scaledWidth - containerWidth) / 2);
    const maxTranslateY = Math.max(0, (scaledHeight - containerHeight) / 2);

    return {
      translateX: Math.max(-maxTranslateX, Math.min(maxTranslateX, translateX)),
      translateY: Math.max(-maxTranslateY, Math.min(maxTranslateY, translateY)),
    };
  };

  // Handle touch start
  const handleTouchStart = (e) => {
    if (!isMobile || isLocal || !screenShareOn) return;

    const touches = e.touches;
    if (touches.length === 1) {
      const touch = touches[0];
      const now = Date.now();
      const timeSinceLastTap = now - touchStateRef.current.lastTouchTime;
      const distanceFromLastTap = Math.sqrt(
        Math.pow(touch.clientX - touchStateRef.current.lastTapX, 2) +
          Math.pow(touch.clientY - touchStateRef.current.lastTapY, 2)
      );

      if (
        timeSinceLastTap < 300 &&
        timeSinceLastTap > 0 &&
        distanceFromLastTap < 50
      ) {
        e.preventDefault();
        if (touchStateRef.current.tapTimeout) {
          clearTimeout(touchStateRef.current.tapTimeout);
          touchStateRef.current.tapTimeout = null;
        }

        const container = containerRef.current;
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const tapX = touch.clientX - containerRect.left;
          const tapY = touch.clientY - containerRect.top;

          if (zoomState.scale > 1) {
            setZoomState({ scale: 1, translateX: 0, translateY: 0 });
          } else {
            const newScale = 2;
            const newTranslateX =
              (-(tapX - containerRect.width / 2) * (newScale - 1)) / newScale;
            const newTranslateY =
              (-(tapY - containerRect.height / 2) * (newScale - 1)) / newScale;

            const constrained = constrainTranslate(
              newScale,
              newTranslateX,
              newTranslateY,
              containerRect.width,
              containerRect.height
            );

            setZoomState({
              scale: newScale,
              translateX: constrained.translateX,
              translateY: constrained.translateY,
            });
          }
        }
        touchStateRef.current.lastTouchTime = 0;
        return;
      }

      touchStateRef.current.lastPanX = touch.clientX;
      touchStateRef.current.lastPanY = touch.clientY;
      touchStateRef.current.startX = touch.clientX;
      touchStateRef.current.startY = touch.clientY;
      touchStateRef.current.hasMoved = false;
      setIsPanning(false); 

      touchStateRef.current.lastTapX = touch.clientX;
      touchStateRef.current.lastTapY = touch.clientY;
      touchStateRef.current.lastTouchTime = now;
    } else if (touches.length === 2) {

      e.preventDefault();

      if (touchStateRef.current.tapTimeout) {
        clearTimeout(touchStateRef.current.tapTimeout);
        touchStateRef.current.tapTimeout = null;
      }

      const distance = getDistance(touches[0], touches[1]);
      const center = getCenter(touches[0], touches[1]);

      touchStateRef.current.initialDistance = distance;
      touchStateRef.current.initialScale = zoomState.scale;
      touchStateRef.current.initialTranslateX = zoomState.translateX;
      touchStateRef.current.initialTranslateY = zoomState.translateY;
      touchStateRef.current.centerX = center.x;
      touchStateRef.current.centerY = center.y;
      setIsZooming(true);
      setIsPanning(false);
    }
  };

  // Handle touch move
  const handleTouchMove = (e) => {
    if (!isMobile || isLocal || !screenShareOn) return;

    const touches = e.touches;

    if (touches.length === 1 && zoomState.scale > 1) {
      const touch = touches[0];
      const deltaX = Math.abs(touch.clientX - touchStateRef.current.startX);
      const deltaY = Math.abs(touch.clientY - touchStateRef.current.startY);
      const movementThreshold = 10; // pixels

      if (
        !touchStateRef.current.hasMoved &&
        (deltaX > movementThreshold || deltaY > movementThreshold)
      ) {
        touchStateRef.current.hasMoved = true;
        setIsPanning(true);
      }

      if (touchStateRef.current.hasMoved) {
        e.preventDefault();
        const panDeltaX = touch.clientX - touchStateRef.current.lastPanX;
        const panDeltaY = touch.clientY - touchStateRef.current.lastPanY;

        const container = containerRef.current;
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const constrained = constrainTranslate(
            zoomState.scale,
            zoomState.translateX + panDeltaX,
            zoomState.translateY + panDeltaY,
            containerRect.width,
            containerRect.height
          );

          setZoomState((prev) => ({
            ...prev,
            translateX: constrained.translateX,
            translateY: constrained.translateY,
          }));
        }

        touchStateRef.current.lastPanX = touch.clientX;
        touchStateRef.current.lastPanY = touch.clientY;
      }
    } else if (touches.length === 2 && isZooming) {
      // Two touches - pinch zoom
      e.preventDefault();
      const distance = getDistance(touches[0], touches[1]);
      const scaleChange = distance / touchStateRef.current.initialDistance;
      const newScale = Math.max(
        1,
        Math.min(5, touchStateRef.current.initialScale * scaleChange)
      );


      const container = containerRef.current;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const centerX = touchStateRef.current.centerX - containerRect.left;
        const centerY = touchStateRef.current.centerY - containerRect.top;

        const scaleDiff = newScale - touchStateRef.current.initialScale;
        const newTranslateX =
          touchStateRef.current.initialTranslateX -
          ((centerX - containerRect.width / 2) * scaleDiff) /
            touchStateRef.current.initialScale;
        const newTranslateY =
          touchStateRef.current.initialTranslateY -
          ((centerY - containerRect.height / 2) * scaleDiff) /
            touchStateRef.current.initialScale;

        const constrained = constrainTranslate(
          newScale,
          newTranslateX,
          newTranslateY,
          containerRect.width,
          containerRect.height
        );

        setZoomState({
          scale: newScale,
          translateX: constrained.translateX,
          translateY: constrained.translateY,
        });
      }
    }
  };

  // Handle touch end
  const handleTouchEnd = (e) => {
    if (!isMobile || isLocal || !screenShareOn) return;

    const touches = e.touches;

    if (touches.length === 0) {
      setIsZooming(false);
      setIsPanning(false);
      touchStateRef.current.hasMoved = false;
    } else if (touches.length === 1) {
      setIsZooming(false);
      const touch = touches[0];
      touchStateRef.current.lastPanX = touch.clientX;
      touchStateRef.current.lastPanY = touch.clientY;
      setIsPanning(touchStateRef.current.hasMoved && zoomState.scale > 1);
    }
  };

  const enterFullscreen = async (el) => {
    if (!el) return;
    try {
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen();
      } else if (el.msRequestFullscreen) {
        await el.msRequestFullscreen();
      }
    } catch (_) {}
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
    } catch (_) {}
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const toggleFullscreen = async () => {
    // If currently in pseudo-fullscreen, exit it
    if (isPseudoFullscreen) {
      document.body.classList.remove("ios-pseudo-fullscreen");
      setIsFullscreen(false);
      setIsPseudoFullscreen(false);
      return;
    }

    // iOS Safari doesn't support requestFullscreen on non-video elements,
    // so use pseudo-fullscreen directly on iOS
    if (isIOS) {
      document.body.classList.add("ios-pseudo-fullscreen");
      setIsFullscreen(true);
      setIsPseudoFullscreen(true);
      return;
    }

    // Desktop/Android: try native Fullscreen API
    try {
      if (document.fullscreenElement === containerRef.current) {
        await exitFullscreen();
      } else if (
        containerRef.current?.requestFullscreen ||
        containerRef.current?.webkitRequestFullscreen ||
        containerRef.current?.msRequestFullscreen
      ) {
        await enterFullscreen(containerRef.current);
      } else {
        throw new Error("Fullscreen API not available");
      }
    } catch (_) {
      // Fallback: use pseudo-fullscreen via portal
      document.body.classList.add("ios-pseudo-fullscreen");
      setIsFullscreen(true);
      setIsPseudoFullscreen(true);
    }
  };

  // The inner content of the presenter view (shared between inline and fullscreen)
  const presenterContent = (
    <div
      ref={containerRef}
      className={`bg-gray-900 relative overflow-hidden ${
        isPseudoFullscreen ? "" : isFullscreen ? "" : `rounded-xl m-2 transition-all duration-300 ease-in-out ${sideBarMode ? "w-[98%]" : "w-full"}`
      }`}
      style={{
        ...(!isFullscreen && {
          height: `${height}px`,
          boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
        }),
        // Native fullscreen: browser handles sizing, just set background
        ...(isFullscreen && !isPseudoFullscreen && {
          width: "100%",
          height: "100%",
          backgroundColor: "#000",
        }),
        // Pseudo-fullscreen (iOS/fallback): we handle positioning via portal
        ...(isPseudoFullscreen && {
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100dvh",
          zIndex: 99999,
          backgroundColor: "#000",
          margin: 0,
          borderRadius: 0,
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
          paddingLeft: "env(safe-area-inset-left)",
          paddingRight: "env(safe-area-inset-right)",
        }),
      }}
    >
      <audio autoPlay playsInline controls={false} ref={audioPlayer} />
      <div
        ref={videoContainerRef}
        className={"video-contain absolute h-full w-full"}
        onDoubleClick={toggleFullscreen}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          touchAction: isMobile && !isLocal && screenShareOn ? "none" : "auto",
          overflow:
            isMobile && !isLocal && screenShareOn && zoomState.scale > 1
              ? "hidden"
              : "visible",
        }}
      >
        {/* Old floating PiP removed — preview is now inline in the presenter area below */}
        {!isLocal && screenShareOn ? (
          <div className="absolute z-30 top-2 right-2 flex items-center gap-2">
            <div className="bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg flex items-center gap-2">
              <ScreenShareIcon style={{ height: 14, width: 14, color: "#00c853" }} />
              <span className="text-white text-xs font-medium">
                {nameTructed(displayName, 20)} is sharing
              </span>
            </div>
            {canStopPresentation && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  publishStopScreenShare(
                    { participantId: presenterId },
                    { persist: false }
                  );
                }}
                title="Stop presentation"
                className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                  <line x1="2" y1="3" x2="22" y2="17" stroke="currentColor"></line>
                </svg>
                Stop Presentation
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFullscreen();
              }}
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              className="bg-black/50 hover:bg-black/70 text-white rounded p-2 transition-colors"
            >
              <img
                src={isFullscreen ? "/icons/compact.svg" : "/icons/wide.svg"}
                alt={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                className="w-5 h-5"
              />
            </button>
          </div>
        ) : null}
        <div
          style={{
            transform:
              isMobile && !isLocal && screenShareOn
                ? `translate(${zoomState.translateX}px, ${zoomState.translateY}px) scale(${zoomState.scale})`
                : "none",
            transformOrigin: "center center",
            transition:
              isZooming || isPanning ? "none" : "transform 0.1s ease-out",
            width: "100%",
            height: "100%",
          }}
        >
          <VideoPlayer
            participantId={presenterId} // Required
            type="share" // "video" or "share"
            containerStyle={{
              height: "100%",
              width: "100%",
            }}
            className="h-full"
            classNameVideo="h-full"
            videoStyle={{
              filter: isLocal ? "blur(1rem)" : undefined,
            }}
          />
        </div>

        {/* Raise Hand Overlay */}
        {hasRaisedHand && (
          <div className="absolute top-2 left-2 z-20">
            <div className="bg-orange-500 rounded-full p-2 shadow-lg">
              <img
                src="/icons/handIcon.png"
                alt="Raised Hand"
                className="w-6 h-6"
              />
            </div>
          </div>
        )}

        <div
          className="bottom-2 left-2 bg-gray-750 p-2 absolute rounded-md flex items-center justify-center"
          style={{
            transition: "all 200ms",
            transitionTimingFunction: "linear",
          }}
        >
          {!micOn ? (
            <MicOffSmallIcon fillcolor="white" />
          ) : micOn && isActiveSpeaker ? (
            <SpeakerIcon />
          ) : (
            <></>
          )}

          {/* <p className="text-sm text-white">
            {isLocal
              ? `You are presenting`
              : `${nameTructed(displayName, 15)} is presenting`}
          </p> */}
        </div>
        {isLocal ? (
          <>
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
              {/* Large screen share preview — shows what participants see */}
              <PresenterScreenPreview localScreenTrack={localScreenTrack} />
              {/* Old PresenterScreenPiP removed: it auto-popped a browser
                  Picture-in-Picture window showing the shared screen itself,
                  which caused the infinite-mirror effect when presenters
                  shared their whole browser tab. The new PresenterCameraPiP
                  (rendered from MeetingContainerNew when an element is
                  actively being presented) shows the presenter's camera
                  instead of the shared content. */}
              <div className="mt-3 flex items-center gap-3">
                <div className="flex items-center gap-2 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                  <ScreenShareIcon style={{ height: 14, width: 14, color: "#00c853" }} />
                  <span className="text-white text-xs font-medium">You are presenting to everyone</span>
                </div>
                <button
                  className="bg-[#7700ff] hover:bg-[#6600dd] text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    mMeeting.toggleScreenShare();
                  }}
                >
                  Stop Presenting
                </button>
              </div>
            </div>
            <CornerDisplayName
              {...{
                isLocal,
                displayName,
                micOn,
                webcamOn,
                isPresenting: true,
                participantId: presenterId,
                isActiveSpeaker,
              }}
            />
          </>
        ) : (
          <></>
        )}
      </div>
      {children}
    </div>
  );

  // Only use portal for pseudo-fullscreen (iOS/fallback) to escape parent transforms.
  // Native fullscreen (desktop) is handled by the browser — no portal needed.
  if (isPseudoFullscreen) {
    return createPortal(presenterContent, document.body);
  }

  return presenterContent;
}
